<template>
  <form-dialog v-model="isActive"
               :sending="sending"
               @confirm="onConfirm">

    <div slot="title">
      <span class="mr-2 left line-height-24">{{ edit ? $t('prenotazioni.azioni.modifica') + ' #' + form.id : $t('prenotazioni.azioni.nuovo') }}</span>
      <alert-chip v-if="edit && form.pendente !== 0" color="warning" class="left">
        {{ $t('prenotazioni.errori.modifiche_da_confermare') }}
      </alert-chip>
    </div>

    <v-flex xs12>
      <icon-label icon="access_time" color="accent">{{ $t('prenotazioni.label.dati_prenotazione') }}</icon-label>
    </v-flex>

    <v-flex xs8>
      <date-picker :label="$t('prenotazioni.label.data')" v-model="form.data_controllo"  :locale="$i18n.locale" required/>
    </v-flex>

    <v-flex xs4>
      <time-picker :label="$t('prenotazioni.label.orario')" v-model="form.ora_controllo" required/>
    </v-flex>

    <v-flex xs12 md4>
      <agricoltori-select :label="$t('label.cliente')" v-model="form.id_agricoltore" required attach/>
    </v-flex>

    <v-flex xs12 sm6 md4>
      <tecnici-select :label="$t('prenotazioni.label.tecnico')" v-model="form.id_tecnico" clearable attach/>
    </v-flex>

    <v-flex xs12 sm6 md4>
      <unita-mobili-select :label="$t('label.unita_mobile')" v-model="form.targa_unita_mobile" attach/>
    </v-flex>

    <v-flex xs12 class="mt-4">
      <icon-label icon="location_on" color="accent">{{ $t('prenotazioni.label.luogo_controllo') }}</icon-label>
    </v-flex>

    <v-flex class="shrink">
      <v-tooltip bottom>
        <v-btn flat icon slot="activator">
          <v-icon>help_outline</v-icon>
        </v-btn>
        <span>{{ $t('prenotazioni.label.inserisci_indirizzo') }}</span>
      </v-tooltip>
    </v-flex>

    <v-flex class="shrink btn--indirizzo">
      <v-btn small color="accent" @click="onIndirizzoClick(0)">{{ $t('label.azienda') }}</v-btn>
      <v-btn small color="accent" @click="onIndirizzoClick(1)" :disabled="!form.id_agricoltore">{{ $t('label.cliente') }}</v-btn>
      <v-btn small color="accent" @click="onIndirizzoClick(2)" :disabled="!form.id_agricoltore">{{ $t('label.convenzione') }}</v-btn>
    </v-flex>

    <v-flex class="shrink">
      <v-progress-circular v-show="loadingAddress" indeterminate size="22" class="mt-2" color="accent" width="3"/>
    </v-flex>

    <v-flex class="grow">
      <v-checkbox :label="$t('prenotazioni.label.compilato_in_convenzione')"
                  v-model="form.compilato_in_convenzione" hide-details class="mt-1"/>
    </v-flex>

    <v-flex xs12>
      <indirizzo-select
        :paese.sync="form.nazione"
        :regione.sync="form.regione"
        :provincia.sync="form.provincia"
        :comune.sync="form.comune"
        :cap.sync="form.cap"
        :frazione.sync="form.frazione"
        :via.sync="form.via"
        :numero-civico.sync="form.numero_civico"
        show-frazione
        required
        attach/>
    </v-flex>

    <v-flex xs12 class="mt-4">
      <icon-label icon="assignment" color="accent">{{ $t('prenotazioni.label.dati_irroratrice') }}</icon-label>
    </v-flex>

    <v-flex xs12>
      <v-layout>
        <v-flex class="shrink">
          <span class="input-label subheading">{{ $t('prenotazioni.label.tipologia_irroratrice') }}</span>
        </v-flex>
        <v-flex class="shrink">
          <v-radio-group v-model="form.dettagli.tipo" row mandatory hide-details single-line>
            <v-radio :label="$t('prenotazioni.label.erbacee')" :value="0"></v-radio>
            <v-radio :label="$t('prenotazioni.label.arboree')" :value="1"></v-radio>
            <v-radio :label="$t('prenotazioni.label.altro') + ':'" :value="2"></v-radio>
          </v-radio-group>
        </v-flex>
        <v-flex>
          <text-field :label="$t('prenotazioni.label.altro')" v-model="form.dettagli.tipo_altro"
                      :disabled="form.dettagli.tipo !== 2" single-line/>
        </v-flex>
      </v-layout>
    </v-flex>

    <v-flex xs12>
        <macchina-select
          :marca.sync="form.dettagli.marca"
          :modello.sync="form.dettagli.modello"
          :numero_serie.sync="form.dettagli.numero_serie"
          :idAgricoltore=this.form.id_agricoltore
          required
          attach/>
    </v-flex>

    <v-flex xs12 sm6 md4>
      <date-picker :label="$t('prenotazioni.label.data_primo_acquisto')" v-model="form.dettagli.data_primo_acquisto"  :locale="$i18n.locale"/>
    </v-flex>

    <v-flex xs12 sm6 md4>
      <text-field :label="$t('prenotazioni.label.anno_costruzione')"
                  v-model="form.dettagli.anno_costruzione" mask="####"/>
    </v-flex>

    <v-flex xs12 sm6 md4>
      <date-picker :label="$t('prenotazioni.label.data_primo_acquisto_presunta')"
                   v-model="form.dettagli.data_primo_acquisto_presunta"  :locale="$i18n.locale"/>
    </v-flex>

    <v-flex xs12>
      <v-checkbox :label="$t('prenotazioni.label.disponibilita_manutenzione')"
                  v-model="form.dettagli.disponibilita_manutenzione" hide-details/>
    </v-flex>

    <v-flex xs12>
      <v-checkbox :label="$t('prenotazioni.label.richiesta_taratura')"
                  v-model="form.dettagli.richiesta_taratura" hide-details/>
    </v-flex>

    <v-flex xs12>
      <v-textarea :label="$t('prenotazioni.label.note')" v-model="form.note" box auto-grow maxlength="500" counter="500"/>
    </v-flex>

    <prenotazione-warning-dialog ref="warningDialog" @confirm="onConfirm(true)"/>

  </form-dialog>
</template>

<script>
import Sendable from '@/mixins/sendable'
import {
    DatePicker,
    TimePicker,
    AgricoltoriSelect,
    TecniciSelect,
    UnitaMobiliSelect,
    IndirizzoSelect,
    MacchinaSelect
} from '@/components/inputs'
import FormDialog from './base/FormDialog'
import PrenotazioneWarningDialog from '@/components/PrenotazioneWarningDialog'

export default {
    name: 'PrenotazioneDialog',
    mixins: [Sendable],
    components: {
        DatePicker,
        TimePicker,
        AgricoltoriSelect,
        TecniciSelect,
        UnitaMobiliSelect,
        IndirizzoSelect,
        MacchinaSelect,
        FormDialog,
        PrenotazioneWarningDialog
    },
    data () {
        return {
            defaultData: this.$schemas().prenotazione,
            loadingAddress: false        
          }
    },
    computed: {
        indirizzo: {
            get () {
                return {
                    nazione: this.form.nazione,
                    regione: this.form.regione,
                    provincia: this.form.provincia,
                    comune: this.form.comune,
                    cap: this.form.cap,
                    frazione: this.form.frazione,
                    via: this.form.via,
                    numero_civico: this.form.numero_civico
                }
            },
            set (val) {
                this.form.nazione = val.nazione
                this.form.regione = val.regione
                this.form.provincia = val.provincia
                this.form.comune = val.comune
                this.form.cap = val.cap
                this.form.frazione = val.frazione || ''
                this.form.via = val.via
                this.form.numero_civico = val.numero_civico
            }
        }
    },
    methods: {
        onIndirizzoClick (val) {
            this.form.compilato_in_convenzione = (val === 2)
            switch (val) {
            case 0: //  Azienda
                this.indirizzo = this.$store.state.global.azienda
                break
            case 1: //  Agricoltore
                this.loadingAddress = true
                this.$api.indirizzo.getByAgricoltore(this.form.id_agricoltore).then(response => {
                    this.loadingAddress = false
                    this.indirizzo = response.data
                })
                break
            case 2: //  Convenzione
                this.loadingAddress = true
                this.$api.indirizzo.getByConvenzioneOfAgricoltore(this.form.id_agricoltore).then(response => {
                    this.loadingAddress = false
                    if (!response.data) {
                        this.$snackbar.show(this.$t('prenotazioni.errori.agricoltore_no_convenzione'), 'error')
                    } else {
                        this.indirizzo = response.data
                    }
                })
                break
            }
        },
        action (force = false) {
            let data = Object.assign({}, this.form)

            data.dettagli = JSON.stringify(this.form.dettagli)
            if (this.edit) {
                if (force) return this.$api.prenotazioni.forceUpdate(data.id, data)
                else return this.$api.prenotazioni.update(data.id, data)
            } else {
                if (force) return this.$api.prenotazioni.forceCreate(data)
                else return this.$api.prenotazioni.create(data)
            }
        },
        async onConfirm (force = false) {
            await new Promise(resolve => setTimeout(resolve, 100));
            this.sending = true
            this.action(force).then(response => {
                if (!this.edit) {
                    //  Salvo l'ultimo indirizzo inserito in modo da renderlo disponibile alla prossima apertura della pagina
                    this.$store.commit('SET_LAST_INDIRIZZO', this.indirizzo)
                }
                this.onSuccess(this.edit ? this.$t('prenotazioni.messaggi.modificato') : this.$t('prenotazioni.messaggi.creato'))
            
              }).catch(e => {
                this.sending = false
                if (e.response && e.response.data[0] && e.response.data[0].errore) {
                    //  C'è un problema con gli orari delle prenotazioni, mostro un alert
                    this.$refs.warningDialog.show(e.response.data)
                } else if (e.response && e.response.status === 404 && e.response.data.message === 'in use') {
                    //  La prenotazione è collegata ad un report già compilato
                    this.onError(this.$t('prenotazioni.errori.riferimenti_attivi_edit'))
                } else {
                    this.onError(this.$t('errori.server'))
                }
            })

            if (MacchinaSelect != null){
                this.addMacchina();
            }
        },
        addMacchina() 
        {
          // Creazione di un oggetto con i dati della macchina selezionata
          let dataMacchina = {
            marca: this.form.dettagli.marca,
            modello: this.form.dettagli.modello,
            numero_serie: this.form.dettagli.numero_serie,
            id_agricoltore: this.form.id_agricoltore
          };

          // Variabile per tenere traccia se la macchina è già presente
          let marcaTrovata = false;
          let modelloTrovato = false;
          let numeroSerieTrovato = false;

          console.log(dataMacchina);
          this.$api.macchine.getForCliente(this.form.id_agricoltore).then(response => {

            // Iterazione su tutte le macchine per verificare se esistono già
            response.data.forEach(element => {
                if (element.marca === dataMacchina.marca) {
                    marcaTrovata = true;
                    if (element.modello === dataMacchina.modello) {
                        modelloTrovato = true;
                        if (element.numero_serie === dataMacchina.numero_serie) {
                            numeroSerieTrovato = true;
                        }
                    }
                }
            });

            if(!marcaTrovata || (marcaTrovata && !modelloTrovato) || (marcaTrovata && modelloTrovato && !numeroSerieTrovato))
            {
              this.$api.macchine.create(dataMacchina);
            }else{
              marcaTrovata = false;
              modelloTrovato = false;
              numeroSerieTrovato = false;
              
              this.$api.macchine.getForAgricoltore(this.form.id_agricoltore).then(response => {

                // Iterazione su tutte le macchine per verificare se esistono già
                response.data.forEach(element => {
                    if (element.marca === dataMacchina.marca) {
                        marcaTrovata = true;
                        if (element.modello === dataMacchina.modello) {
                            modelloTrovato = true;
                            if (element.numero_serie === dataMacchina.numero_serie) {
                                numeroSerieTrovato = true;
                            }
                        }
                    }
                });

                if(!marcaTrovata || (marcaTrovata && !modelloTrovato) || (marcaTrovata && modelloTrovato && !numeroSerieTrovato))
                {
                  this.$api.macchine.createLocal(dataMacchina);
                }
              }).catch(error => {
                console.log("Nessuna macchina trovata");
                if(error.response.status == 404) this.$api.macchine.create(dataMacchina);
                console.log(error);
              })
            }
          }).catch(error => {
            console.log("Nessuna macchina trovata");
            if(error.response.status == 404) this.$api.macchine.create(dataMacchina);
            console.log(error);
          })
        },
        afterInitialize (prenotazione) {

            if (prenotazione && prenotazione.dettagli) {
                this.$set(this.form, 'dettagli', JSON.parse(prenotazione.dettagli))
                console.log(this.form)
            } else {
                this.$set(this.form, 'dettagli', { tipo: 0 })
            }
            //  Imposto l'ultimo indirizzo inserito se c'è
            if (!this.edit && this.$store.state.global.lastIndirizzo) {
                this.indirizzo = this.$store.state.global.lastIndirizzo
            }
        }
    }
}
</script>
<style lang="scss" scoped>

  .btn--indirizzo {
    button:nth-child(1) {
      border-bottom-right-radius: 0 !important;
      border-top-right-radius: 0 !important;
    }

    button:nth-child(2) {
      border-radius: 0 !important;
    }

    button:nth-child(3) {
      border-bottom-left-radius: 0 !important;
      border-top-left-radius: 0 !important;
    }

    .v-btn {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

  }

  .input-label {
    line-height: 64px;
    color: rgba(0, 0, 0, 0.54);
  }

</style>
